import { useState, useEffect, useCallback } from 'react';
import { Page, FormLayout, TextField, Button, Layout, LegacyCard } from "@shopify/polaris";
import { useParams, useHistory } from "react-router-dom";
import Api from '../../helpers/api';
import {getFormError} from "../../helpers/utils";
import {useToast} from "@shopify/app-bridge-react";
import {CountrySelector, StateSelector} from "../Selectors";

export default function BillingAddressesForm() {
    const { id } = useParams();
    const history = useHistory();

    const [form, setForm] = useState({
        id: null,
        business_name: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        country: '',
        zip_code: '',
        contact_email: '',
        contact_phone: '',
        contact_fax: '',
        notes: '',
        created_at: '',
        updated_at: '',
    });

    const [saving, setSaving] = useState(false);
    const toast = useToast();

    const [errors, setErrors] = useState({});
    const showError = name => getFormError(errors, name);

    const loadAddress = useCallback(async () => {
        if (!id) return;
        try {
            const res = await Api.getBillingAddress(id);
            res.data = res.data.data || {};

            setForm({
                id: res.data.id || null,
                business_name: res.data.business_name || '',
                external_id: res.data.external_id || '',
                address_line_1: res.data.address_line_1 || '',
                address_line_2: res.data.address_line_2 || '',
                city: res.data.city || '',
                state: res.data.state || '',
                country: res.data.country || '',
                zip_code: res.data.zip_code || '',
                contact_email: res.data.contact_email || '',
                contact_phone: res.data.contact_phone || '',
                contact_fax: res.data.contact_fax || '',
                notes: res.data.notes || '',
                created_at: res.data.created_at ? new Date(res.data.created_at).toLocaleString() : '',
                updated_at: res.data.updated_at ? new Date(res.data.updated_at).toLocaleString() : '',
            });
        } catch (err) {
            console.error("Error loading billing address", err);
        }
    }, [id]);

    useEffect(() => {
        loadAddress();
    }, [loadAddress]);

    const handleChange = (field) => (value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        setErrors({});
        try {
            if (id) {
                await Api.updateBillingAddress(id, form);
            } else {
                await Api.createBillingAddress(form);
            }
            toast.show(id ? "Billing address updated successfully." : "Billing address created successfully.");
            history.push('/billingAddresses');
        } catch (error) {

            if (error.response.status == 422) {
                setErrors(error.response.data.errors);
                if (error.response.data.errors.shopify_error) {
                    toast.show(error.response.data.errors.shopify_error[0], {isError: true});
                } else {
                    toast.show("There has been an error processing the form.", {isError: true});
                    setErrors(error.response.data.errors);
                }
            } else {
                toast.show("There has been an error processing the form.", {isError: true});
            }

            if (error.response.status != 422) {
                throw error;
            }
        }
        setSaving(false);
    };

    return (
        <Page
            title={id ? "Edit Billing Address" : "New Billing Address"}
            backAction={{ content: 'Back', onAction: () => history.goBack() }}
            primaryAction={{
                content: id ? "Update" : "Save",
                onAction: handleSave,
                loading: saving
            }}
            secondaryActions={[
                { content: 'Show Customers for this Bill to', onAction: () => history.push(`/customers?bill_to_id=${form.external_id ? form.external_id : id}`) },
                {
                    content: 'Delete',
                    onAction: () => {
                        if (window.confirm("Are you sure you want to delete this billing address?")) {
                            Api.deleteBillingAddress(id)
                                .then(() => {
                                    history.push('/billingAddresses');
                                    toast.show("Billing address deleted successfully.");
                                })
                                .catch(err => {
                                    console.error("Error deleting billing address", err);
                                    toast.show("Error deleting billing address.", {isError: true});
                                });
                        }
                    }
                },
            ]}
        >
            <Layout>
                <Layout.Section>
                    <LegacyCard sectioned>
                        <FormLayout>
                            {id && <TextField label="Id" value={form.external_id} disabled/>}
                            <TextField label="Customer" value={form.business_name} onChange={handleChange('business_name')} autoComplete="off" error={showError('business_name')}/>
                            <TextField label="Address Line 1" value={form.address_line_1} onChange={handleChange('address_line_1')} autoComplete="off" error={showError('address_line_1')} />
                            <TextField label="Address Line 2" value={form.address_line_2} onChange={handleChange('address_line_2')} autoComplete="off" error={showError('address_line_2')} />
                            <TextField label="City" value={form.city} onChange={handleChange('city')} autoComplete="off" error={showError('city')} />
                            <CountrySelector onChange={handleChange('country')}value={form.country} error={showError('country')} />
                            <StateSelector onChange={handleChange('state')} value={form.state} error={showError('state')} selectedCountry={form.country} />
                            <TextField label="Zip Code" value={form.zip_code} onChange={handleChange('zip_code')} autoComplete="off" error={showError('zip_code')} />
                            <TextField label="Email" value={form.contact_email} onChange={handleChange('contact_email')} type="email" autoComplete="off" error={showError('contact_email')} />
                            <TextField label="Phone" value={form.contact_phone} onChange={handleChange('contact_phone')} autoComplete="off" error={showError('contact_phone')} />
                            <TextField label="Fax" value={form.contact_fax} onChange={handleChange('contact_fax')} autoComplete="off" error={showError('contact_fax')} />
                            <TextField label="Notes" value={form.notes} onChange={handleChange('notes')} multiline={4} autoComplete="off" error={showError('notes')} />
                            {id &&<TextField label="Created At" value={form.created_at} disabled/>}
                            {id &&<TextField label="Updated At" value={form.updated_at} disabled/>}
                        </FormLayout>
                    </LegacyCard>
                </Layout.Section>
            </Layout>
        </Page>
    );
}
